// src/firebase/config.js
import { initializeApp } from 'firebase/app';
import { getFirestore, connectFirestoreEmulator } from 'firebase/firestore';
import { getAuth, connectAuthEmulator } from 'firebase/auth';
import { initializeAppCheck, ReCaptchaEnterpriseProvider, ReCaptchaV3Provider } from 'firebase/app-check';

// Emuladores locales (npm run start:emu): Firestore y Auth apuntan a
// localhost en vez de producción, para probar la estructura nueva sin
// tocar datos reales. Apagado por defecto — sin la variable, nada cambia.
export const USE_EMULATORS = process.env.REACT_APP_USE_EMULATORS === 'true';

const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID
};

// Validar configuración
const missingVars = [];
if (!firebaseConfig.apiKey) missingVars.push('REACT_APP_FIREBASE_API_KEY');
if (!firebaseConfig.authDomain) missingVars.push('REACT_APP_FIREBASE_AUTH_DOMAIN');
if (!firebaseConfig.projectId) missingVars.push('REACT_APP_FIREBASE_PROJECT_ID');

if (missingVars.length > 0) {
  console.error('❌ Missing Firebase env variables:', missingVars);
  throw new Error(`Missing Firebase configuration: ${missingVars.join(', ')}`);
}

// Inicializar
const app = initializeApp(firebaseConfig);
const db = getFirestore(app);
const auth = getAuth(app);

if (USE_EMULATORS) {
  connectFirestoreEmulator(db, '127.0.0.1', 8080);
  connectAuthEmulator(auth, 'http://127.0.0.1:9099', { disableWarnings: true });
}

// App Check (SECURITY.md, S-04). Solo se activa si hay clave de sitio:
//   REACT_APP_FIREBASE_APPCHECK_SITE_KEY   clave de sitio de reCAPTCHA (pública)
//   REACT_APP_FIREBASE_APPCHECK_PROVIDER   'v3' (por defecto) o 'enterprise'
//   REACT_APP_FIREBASE_APPCHECK_DEBUG      'true' solo en desarrollo: imprime un token
//                                          de depuración para registrarlo en la consola
// El cliente solo ENVÍA el token. Que se exija o no se decide en la consola de
// Firebase: dejar Auth y Firestore en "Monitor" (no bloquea) hasta revisar las
// métricas, y pasar a "Enforce" solo después. Sin emuladores: allí no aplica.
const appCheckSiteKey = process.env.REACT_APP_FIREBASE_APPCHECK_SITE_KEY;
if (appCheckSiteKey && !USE_EMULATORS) {
  if (process.env.REACT_APP_FIREBASE_APPCHECK_DEBUG === 'true') {
    // Debe definirse ANTES de initializeAppCheck.
    // eslint-disable-next-line no-restricted-globals
    self.FIREBASE_APPCHECK_DEBUG_TOKEN = true;
  }
  const provider = process.env.REACT_APP_FIREBASE_APPCHECK_PROVIDER === 'enterprise'
    ? new ReCaptchaEnterpriseProvider(appCheckSiteKey)
    : new ReCaptchaV3Provider(appCheckSiteKey);
  initializeAppCheck(app, { provider, isTokenAutoRefreshEnabled: true });
}

console.log('✅ Firebase initialized:', {
  projectId: firebaseConfig.projectId,
  authDomain: firebaseConfig.authDomain,
  emulators: USE_EMULATORS
});

// Exportado para adjusterService.js: crear la cuenta de Auth de
// un ajustador nuevo requiere una segunda instancia de la app de Firebase
// (misma config, otro nombre) para no pisar la sesión del admin logueado.
export { app, db, auth, firebaseConfig };